import React, { useCallback, useEffect, useState } from 'react';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const formatUptime = (seconds = 0) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

export default function AdminSystem() {
  const [summary, setSummary] = useState(null);
  const [audit, setAudit] = useState({ items: [], page: 1, pages: 1, total: 0 });
  const [method, setMethod] = useState('');
  const [result, setResult] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (method) params.method = method;
      if (result) params.success = result;
      const [summaryRes, auditRes] = await Promise.all([
        api.get('/system/summary'),
        api.get('/system/audit', { params }),
      ]);
      setSummary(summaryRes.data);
      setAudit(auditRes.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not load system status');
    } finally {
      setLoading(false);
    }
  }, [method, page, result]);

  useEffect(() => { load(); }, [load]);

  if (loading && !summary) return <div className="p-8 text-[#8A796C]">Loading system status...</div>;

  const statusItems = summary ? [
    ['Database', summary.database.connected ? 'Connected' : 'Disconnected', summary.database.connected],
    ['SMTP', summary.services.smtpConfigured ? 'Configured' : 'Needs setup', summary.services.smtpConfigured],
    ['Razorpay', summary.services.razorpayConfigured ? 'Configured' : 'Needs setup', summary.services.razorpayConfigured],
    ['Cash on Delivery', summary.services.codEnabled ? 'Enabled' : 'Disabled', summary.services.codEnabled],
  ] : [];

  return (
    <div className="space-y-6 max-w-7xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#2B1810]">System & Audit</h1>
          <p className="text-xs text-[#8A796C]">Runtime health, service readiness, access totals, and immutable admin activity.</p>
        </div>
        <button type="button" onClick={load} disabled={loading} className="btn-primary text-xs">
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {summary && (
        <>
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {statusItems.map(([label, value, healthy]) => (
              <div key={label} className="bg-white border border-[#E8DFD8] rounded-md p-4">
                <p className="text-[10px] uppercase font-bold text-[#8A796C]">{label}</p>
                <p className={`mt-2 text-sm font-bold ${healthy ? 'text-emerald-700' : 'text-amber-700'}`}>{value}</p>
              </div>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <div className="bg-white border border-[#E8DFD8] rounded-md p-4"><p className="text-[10px] uppercase font-bold text-[#8A796C]">Active Admins</p><p className="mt-2 text-xl font-bold">{summary.security.activeAdminCount} / {summary.security.adminCount}</p></div>
            <div className="bg-white border border-[#E8DFD8] rounded-md p-4"><p className="text-[10px] uppercase font-bold text-[#8A796C]">Roles</p><p className="mt-2 text-xl font-bold">{summary.security.roleCount}</p></div>
            <div className="bg-white border border-[#E8DFD8] rounded-md p-4"><p className="text-[10px] uppercase font-bold text-[#8A796C]">Audit Events</p><p className="mt-2 text-xl font-bold">{summary.security.auditCount}</p></div>
            <div className="bg-white border border-[#E8DFD8] rounded-md p-4"><p className="text-[10px] uppercase font-bold text-[#8A796C]">Runtime</p><p className="mt-2 text-sm font-bold">{summary.runtime.nodeVersion} · {formatUptime(summary.runtime.uptimeSeconds)} · {summary.runtime.memoryMb} MB</p></div>
          </div>
        </>
      )}

      <section className="bg-white border border-[#E8DFD8] rounded-md overflow-hidden">
        <div className="p-4 border-b border-[#E8DFD8] flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-heading text-sm font-bold">Admin Activity Log</h2>
            <p className="text-[11px] text-[#8A796C]">{audit.total} recorded changes</p>
          </div>
          <div className="flex gap-2">
            <select value={method} onChange={(e) => { setMethod(e.target.value); setPage(1); }} className="input-field py-2 text-xs">
              <option value="">All actions</option>
              <option value="POST">Create</option>
              <option value="PUT">Replace</option>
              <option value="PATCH">Update</option>
              <option value="DELETE">Delete</option>
            </select>
            <select value={result} onChange={(e) => { setResult(e.target.value); setPage(1); }} className="input-field py-2 text-xs">
              <option value="">All results</option>
              <option value="true">Successful</option>
              <option value="false">Rejected</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#FAF8F5] text-left text-[#6B5B50]">
              <tr><th className="p-3">Time</th><th className="p-3">Admin</th><th className="p-3">Action</th><th className="p-3">Resource</th><th className="p-3">Result</th></tr>
            </thead>
            <tbody>
              {audit.items.map((item) => (
                <tr key={item._id} className="border-t border-[#EFE8E2]">
                  <td className="p-3 whitespace-nowrap">{new Date(item.createdAt).toLocaleString()}</td>
                  <td className="p-3"><span className="font-semibold block">{item.actor?.name || item.actorName || 'Unknown admin'}</span><span className="text-[#8A796C]">{item.actor?.email || item.actorEmail}</span></td>
                  <td className="p-3 font-bold">{item.method}</td>
                  <td className="p-3 font-mono text-[11px]">{item.resource}</td>
                  <td className="p-3"><span className={`px-2 py-1 rounded-full font-bold ${item.success ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>{item.statusCode}</span></td>
                </tr>
              ))}
              {!audit.items.length && <tr><td colSpan="5" className="p-8 text-center text-[#8A796C]">No activity matches these filters.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="p-3 border-t border-[#E8DFD8] flex justify-end items-center gap-3">
          <button type="button" className="btn-dark text-xs" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Previous</button>
          <span className="text-xs">Page {audit.page} of {audit.pages}</span>
          <button type="button" className="btn-dark text-xs" disabled={page >= audit.pages} onClick={() => setPage((value) => value + 1)}>Next</button>
        </div>
      </section>
    </div>
  );
}
